import { useState, useRef, useEffect } from 'react'
import { useI18n } from '../../i18n'
import { sendSSE } from '../sseApi'
import API from '../../helper/api'
import { Bot, X, Send } from 'lucide-react'

interface Props {
  shortcode: string
  botName: string
  avatarUrl?: string
  welcomeMessage: string
  onClose: () => void
}

interface VisitorMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
}

export default function ButlerChat({ shortcode, botName, avatarUrl, welcomeMessage, onClose }: Props) {
  const { lang } = useI18n()
  const [messages, setMessages] = useState<VisitorMessage[]>([])
  const [input, setInput] = useState('')
  const [isStreaming, setIsStreaming] = useState(false)
  const [streamingText, setStreamingText] = useState('')
  const [sessionId] = useState(() => `visitor-${Date.now()}-${Math.random().toString(36).slice(2)}`)
  const endRef = useRef<HTMLDivElement>(null)
  const abortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, streamingText])

  useEffect(() => {
    return () => {
      abortRef.current?.abort()
    }
  }, [])

  const handleSend = (customInput?: string) => {
    const text = customInput || input.trim()
    if (!text || isStreaming) return

    const userMsg: VisitorMessage = { id: `u-${Date.now()}`, role: 'user', content: text }
    setMessages(prev => [...prev, userMsg])
    setInput('')
    setIsStreaming(true)
    setStreamingText('')

    const controller = sendSSE(
      { shortcode, message: text, sessionId },
      API.GET_WEBSITE_BUTLER_CHAT,
      (chunk: string) => { setStreamingText(prev => prev + chunk) },
      () => {
        setStreamingText(prev => {
          const final = prev
          setMessages(prevMsgs => [...prevMsgs, { id: `a-${Date.now()}`, role: 'assistant', content: final }])
          return ''
        })
        setIsStreaming(false)
        abortRef.current = null
      },
      () => {
        setStreamingText(prev => {
          if (prev) {
            setMessages(prevMsgs => [...prevMsgs, { id: `a-${Date.now()}`, role: 'assistant', content: prev }])
          }
          return ''
        })
        setIsStreaming(false)
        abortRef.current = null
      }
    )
    abortRef.current = controller
  }

  return (
    <div className="h-full flex flex-col bg-white rounded-2xl border border-black/[0.06] shadow-[0_12px_40px_-8px_rgba(0,0,0,0.15)] overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-black/[0.04] bg-white">
        <div className="flex items-center gap-2">
          {avatarUrl ? (
            <img src={avatarUrl} alt="" className="w-7 h-7 rounded-full object-cover" />
          ) : (
            <div className="w-7 h-7 rounded-full bg-orange-500/10 flex items-center justify-center">
              <Bot className="h-4 w-4 text-orange-500" />
            </div>
          )}
          <span className="text-sm font-medium text-[#1a1a1a]">{botName}</span>
        </div>
        <button onClick={onClose} className="p-1 rounded-lg text-gray-300 hover:text-gray-500 hover:bg-gray-50">
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {/* Welcome */}
        {messages.length === 0 && !isStreaming && (
          <div className="flex items-start gap-2">
            <div className="w-6 h-6 rounded-full bg-orange-500/10 flex items-center justify-center shrink-0 mt-0.5">
              <Bot className="h-3 w-3 text-orange-500" />
            </div>
            <div className="max-w-[80%] px-3 py-2 rounded-xl rounded-bl-md bg-orange-50 text-xs text-gray-600">
              {welcomeMessage}
            </div>
          </div>
        )}

        {messages.map(msg => (
          <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {msg.role === 'assistant' && (
              <div className="w-6 h-6 rounded-full bg-orange-500/10 flex items-center justify-center shrink-0 mt-0.5 mr-1.5">
                <Bot className="h-3 w-3 text-orange-500" />
              </div>
            )}
            <div className={`max-w-[80%] px-3 py-2 rounded-xl text-xs leading-relaxed whitespace-pre-wrap ${
              msg.role === 'user'
                ? 'bg-orange-500 text-white rounded-br-md'
                : 'bg-gray-50 text-gray-600 rounded-bl-md'
            }`}>
              {msg.content}
            </div>
          </div>
        ))}

        {isStreaming && streamingText && (
          <div className="flex justify-start">
            <div className="w-6 h-6 rounded-full bg-orange-500/10 flex items-center justify-center shrink-0 mt-0.5 mr-1.5">
              <Bot className="h-3 w-3 text-orange-500" />
            </div>
            <div className="max-w-[80%] px-3 py-2 rounded-xl rounded-bl-md bg-gray-50 text-xs text-gray-600 whitespace-pre-wrap">
              {streamingText}
              <span className="inline-block w-1 h-3 bg-orange-500 animate-pulse ml-0.5 align-middle" />
            </div>
          </div>
        )}

        <div ref={endRef} />
      </div>

      {/* Input */}
      <div className="px-3 py-3 border-t border-black/[0.04]">
        <div className="flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() } }}
            placeholder={lang === 'zh' ? '输入问题...' : 'Ask a question...'}
            disabled={isStreaming}
            className="flex-1 h-9 px-3 rounded-xl border border-black/[0.06] text-xs placeholder:text-gray-300 focus:border-orange-200 focus:outline-none disabled:opacity-50"
          />
          <button onClick={() => handleSend()} disabled={!input.trim() || isStreaming}
            className="shrink-0 h-9 w-9 p-0 bg-orange-500 hover:bg-orange-600 text-white rounded-xl flex items-center justify-center disabled:opacity-40 transition-all">
            <Send className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  )
}
